import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { supabase } from '../lib/supabase-auth'
import { ExclamationTriangleIcon, ShieldExclamationIcon, CheckCircleIcon } from '@heroicons/react/24/outline'

export default function MFARecovery() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [step, setStep] = useState<'initiate' | 'verify_email' | 'verify_code' | 'complete'>('initiate')
  const [email, setEmail] = useState('')
  const [verificationCode, setVerificationCode] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [lockedUntil, setLockedUntil] = useState<Date | null>(null)

  // Check if coming from email link
  useEffect(() => {
    const token = searchParams.get('token')
    const emailParam = searchParams.get('email')
    
    if (token && emailParam) {
      setEmail(emailParam)
      setStep('verify_email')
      verifyEmailToken(token, emailParam)
    }
  }, [searchParams])

  const initiateRecovery = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setSuccess(null)
    setLoading(true)

    try {
      const { data, error } = await supabase.functions.invoke('mfa-recovery', {
        body: {
          email,
          action: 'initiate'
        }
      })

      if (error) throw error

      if (data.success) {
        setSuccess(data.message)
        // Don't automatically move to next step - user needs to check email
      } else {
        setError(data.error)
        if (data.locked_until) {
          setLockedUntil(new Date(data.locked_until))
        }
      }
    } catch (err) {
      setError('Failed to initiate recovery. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const verifyEmailToken = async (token: string, emailParam: string) => {
    setLoading(true)
    setError(null)

    try {
      const { data, error } = await supabase.functions.invoke('mfa-recovery', {
        body: {
          email: emailParam,
          token,
          action: 'verify_email'
        }
      })

      if (error) throw error

      if (data.success) {
        setSuccess('Email verified! Check your email for the verification code.')
        setStep('verify_code')
      } else {
        setError(data.error)
        setStep('initiate')
      }
    } catch (err) {
      setError('Invalid or expired recovery link.')
      setStep('initiate')
    } finally {
      setLoading(false)
    }
  }

  const verifyCodeAndReset = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)

    const token = searchParams.get('token')
    if (!token) {
      setError('Invalid recovery session')
      return
    }

    try {
      const { data, error } = await supabase.functions.invoke('mfa-recovery', {
        body: {
          email,
          token,
          verification_code: verificationCode,
          action: 'reset_mfa'
        }
      })

      if (error) throw error

      if (data.success) {
        setSuccess(data.message)
        setStep('complete')
        
        // Store user type for redirect
        if (data.userType) {
          sessionStorage.setItem('mfa_recovery_user_type', data.userType)
        }
      } else {
        setError(data.error)
      }
    } catch (err) {
      setError('Failed to verify code. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const formatTimeRemaining = (until: Date) => {
    const now = new Date()
    const diff = until.getTime() - now.getTime()
    const minutes = Math.ceil(diff / 60000)
    return `${minutes} minute${minutes !== 1 ? 's' : ''}`
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 flex items-center justify-center p-4">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-red-500/10 via-transparent to-transparent"></div>
      </div>

      <div className="relative bg-white/95 backdrop-blur-sm rounded-xl shadow-2xl p-8 max-w-md w-full">
        {/* Security Warning */}
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-start gap-3">
            <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800">
              <p className="font-semibold mb-1">Security Notice</p>
              <p>Two-factor authentication recovery should only be used if you've lost access to your authenticator app. This process will disable 2FA on your account.</p>
            </div>
          </div>
        </div>

        {/* Header */}
        <div className="text-center mb-8">
          <ShieldExclamationIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900">2FA Recovery</h2>
          <p className="text-gray-600 mt-2">
            {step === 'initiate' && 'Recover access to your account'}
            {step === 'verify_email' && 'Verifying your email...'}
            {step === 'verify_code' && 'Enter verification code'}
            {step === 'complete' && 'Recovery complete'}
          </p>
        </div>

        {/* Account Locked Message */}
        {lockedUntil && lockedUntil > new Date() && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start gap-3">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-red-800">
                <p className="font-semibold">Account Temporarily Locked</p>
                <p>Too many recovery attempts. Please try again in {formatTimeRemaining(lockedUntil)}.</p>
              </div>
            </div>
          </div>
        )}

        {/* Step 1: Initiate Recovery */}
        {step === 'initiate' && (
          <form onSubmit={initiateRecovery} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-amber-500 focus:border-amber-500 text-gray-900"
                placeholder="Enter your email"
                required
                autoFocus
              />
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
                {error}
              </div>
            )}

            {success && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-600">
                {success}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || (lockedUntil && lockedUntil > new Date())}
              className="w-full px-4 py-3 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium"
            >
              {loading ? 'Sending...' : 'Send Recovery Email'}
            </button>

            <div className="text-center text-sm text-gray-600">
              <p>Remember your authenticator?</p>
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="text-amber-600 hover:text-amber-700 font-medium"
              >
                Back to Login
              </button>
            </div>
          </form>
        )}

        {/* Step 2: Verify Email (loading state) */}
        {step === 'verify_email' && loading && (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500 mx-auto mb-4"></div>
            <p className="text-gray-600">Verifying your email...</p>
          </div>
        )}

        {/* Step 3: Enter Verification Code */}
        {step === 'verify_code' && (
          <form onSubmit={verifyCodeAndReset} className="space-y-6">
            <div>
              <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-2">
                Verification Code
              </label>
              <input
                type="text"
                id="code"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-amber-500 focus:border-amber-500 text-center text-lg font-mono text-gray-900"
                placeholder="000000"
                maxLength={6}
                required
                autoFocus
              />
              <p className="mt-2 text-xs text-gray-500 text-center">
                Check your email for the 6-digit code
              </p>
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || verificationCode.length !== 6}
              className="w-full px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium"
            >
              {loading ? 'Verifying...' : 'Disable 2FA & Reset'}
            </button>

            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
              <p className="font-semibold mb-1">⚠️ Warning</p>
              <p>This will disable two-factor authentication on your account. You'll need to set it up again after logging in.</p>
            </div>
          </form>
        )}

        {/* Step 4: Complete */}
        {step === 'complete' && (
          <div className="text-center">
            <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Recovery Complete</h3>
            <p className="text-gray-600 mb-6">{success}</p>
            
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg mb-6">
              <p className="text-sm text-blue-800">
                <strong>Important:</strong> Two-factor authentication has been disabled. For your security, please re-enable it after logging in.
              </p>
            </div>

            <button
              onClick={() => {
                const userType = sessionStorage.getItem('mfa_recovery_user_type')
                sessionStorage.removeItem('mfa_recovery_user_type')
                
                // Redirect based on user type
                if (userType === 'client') {
                  navigate('/client-portal/login')
                } else {
                  navigate('/login')
                }
              }}
              className="w-full px-4 py-3 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors font-medium"
            >
              {sessionStorage.getItem('mfa_recovery_user_type') === 'client' 
                ? 'Go to Client Login' 
                : 'Go to Advisor Login'}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}